import { useMemo, useState } from 'react';

import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, IconButton } from '@mui/material';
import { omit } from '@npi/buddy-utils';
import { AddMerchantApplication, Merchant } from '@prisma/client';

import { useMutation } from '@redwoodjs/web';

import { PrincipalForm } from 'src/components';
import { Flex } from 'src/components/Flex';
import { ModalBox } from 'src/components/ModalContent/Modal';
import { NPIDataGrid } from 'src/components/NPIDataGrid/NPIDataGrid';
import { createColumnDef } from 'src/components/NPIDataGrid/NPIDataGrid.utils';
import { PrincipalType } from 'src/components/PrincipalForm/PrincipalForm';
import ReusableGrid from 'src/components/ReusableGrid';
import { ContactInformation } from 'src/components/SubNavigation/components/Contact';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { UPDATE_MERCHANT_PRINCIPALS } from 'src/graphql/mutations/addMerchant.mutation';
import { principalInitialValue } from 'src/pages/AddMerchantPage/steps/MerchantInformation/components/Principal';
import buildContact from 'src/utils/buildContact';
import { handlePrincipalSubmit, PrincipalModalTitles } from 'src/utils/ViewMerchant';

const PrincipalTable = ({
    merchant,
    refetchMerchant,
}: {
    merchant: Merchant & {
        application: AddMerchantApplication;
    };
    refetchMerchant: () => void;
}) => {
    const [principalModalTitle, setPrincipalModalTitle] = useState('');
    const [principalInitialValues, setPrincipalInitialValues] = useState<PrincipalType>();
    const [openPrincipalModal, setOpenPrincipalModal] = useState(false);
    const [principalIndex, setPrincipalIndex] = useState<number | undefined>(undefined);
    const [newBillingContactMessage, setNewBillingContactMessage] = useState<string>('');

    const [updatePrincipals] = useMutation(UPDATE_MERCHANT_PRINCIPALS, {
        onCompleted: ({
            updateMerchantPrincipals,
        }: {
            updateMerchantPrincipals: {
                fullName: string;
            };
        }) => {
            if (newBillingContactMessage) {
                showSuccessToast(newBillingContactMessage);
            } else {
                showSuccessToast(
                    `Contact details have been created for ${updateMerchantPrincipals.fullName}.`
                );
            }
            refetchMerchant();
            handleCancelPrincipalModal();
        },
        onError: () => {
            showErrorToast('Error Updating Contact Details');
        },
    });

    const contactInformation: ContactInformation[] = useMemo(() => {
        const principals = merchant?.application?.merchantInformation?.principals ?? [];
        return buildContact(principals);
    }, [merchant]);

    const handleAddPrincipalInitialValues = () => {
        const personalInfoInitialValues = omit(principalInitialValue, [
            'addresses',
            'isBeneficialOwner',
            'percentageOfOwnership',
            'position',
        ]);
        setPrincipalInitialValues({
            newPrincipalIndex: contactInformation.length + 1,
            principal: personalInfoInitialValues,
        });
    };

    const handleAddPrincipalModal = () => {
        setPrincipalModalTitle(PrincipalModalTitles.ADD);
        setOpenPrincipalModal(true);
        handleAddPrincipalInitialValues();
    };

    const handleCancelPrincipalModal = () => {
        setPrincipalModalTitle('');
        setPrincipalInitialValues(undefined);
        handleAddPrincipalInitialValues();
        setOpenPrincipalModal(false);
        setNewBillingContactMessage('');
    };

    const handleEditPrincipalModal = (index: number) => {
        setPrincipalIndex(index);
        setPrincipalModalTitle(PrincipalModalTitles.EDIT);
        const { principals } = merchant.application.merchantInformation;
        const principalInitialValues = principals[index];

        setPrincipalInitialValues({
            principals,
            newPrincipalIndex: index + 1,
            principal: principalInitialValues,
        });
        setOpenPrincipalModal(true);
    };

    const columns = [
        createColumnDef({
            field: 'name',
            headerName: 'Name',
            disableColumnMenu: true,
        }),
        createColumnDef({ field: 'type', headerName: 'Type', disableColumnMenu: true }),
        createColumnDef({
            field: 'phone',
            headerName: 'Telephone Number',
            disableColumnMenu: true,
        }),
        createColumnDef({ field: 'email', headerName: 'Email', disableColumnMenu: true }),
        createColumnDef({
            field: 'action',
            headerName: 'Action',
            flex: 0,
            minWidth: 160,
            disableColumnMenu: true,
            renderCell: (params) => {
                return (
                    <Flex gap="8px">
                        <IconButton aria-label="edit">
                            <EditOutlinedIcon
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditPrincipalModal(params.row.id - 1);
                                }}
                            />
                        </IconButton>
                    </Flex>
                );
            },
        }),
    ];

    const rows = () => {
        const principals = merchant?.application?.merchantInformation?.principals ?? [];
        return principals.map((principal, index) => {
            return {
                id: index + 1,
                name: `${principal.firstName} ${principal.lastName}`,
                type: principal.isBillingContact ? 'Billing Contact' : '',
                phone: principal.mobileNumber || '',
                email: principal.email || '',
            };
        });
    };

    return (
        <>
            <ReusableGrid
                title="Principals"
                xs={12}
                addRecordButtonConfig={{
                    addRecordButtonText: PrincipalModalTitles.ADD,
                    addRecordCallBack: handleAddPrincipalModal,
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        '& .MuiDataGrid-toolbarContainer': { display: 'none' },
                    }}
                >
                    <NPIDataGrid
                        rows={rows()}
                        columns={columns}
                        getRowId={(row) => {
                            return row.id;
                        }}
                        key="PrincipalsTable"
                        dataTestId="PrincipalsDetails"
                    />
                </Box>
            </ReusableGrid>
            <ModalBox
                handleCloseModal={handleCancelPrincipalModal}
                open={openPrincipalModal}
                title={principalModalTitle}
                widthSize="100%"
            >
                <PrincipalForm
                    initialValues={principalInitialValues}
                    onCancel={handleCancelPrincipalModal}
                    onSubmit={async (formik) => {
                        handlePrincipalSubmit(formik, {
                            merchantDashboard: merchant,
                            principalModalTitle,
                            principalIndex,
                            setNewBillingContactMessage,
                            updatePrincipals,
                        });
                    }}
                />
            </ModalBox>
        </>
    );
};

export default PrincipalTable;
